import { useState, useEffect } from 'react'
import styles from './ProjectTaskSelectionModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function AddMemberToProjectModal({ 
    isOpen, 
    onClose, 
    onSave, 
    member,
    apiToken,
    memberProjects = []
}) {
    const [availableProjects, setAvailableProjects] = useState([])
    const [selectedProject, setSelectedProject] = useState(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    // Add escape key functionality
    useEscapeKey(isOpen, onClose, !loading && !saving)

    useEffect(() => {
        if (isOpen && apiToken) {
            loadAvailableProjects()
        }
    }, [isOpen, apiToken])

    useEffect(() => {
        if (isOpen) {
            setSelectedProject(null)
        }
    }, [isOpen])

    const loadAvailableProjects = async () => {
        try {
            setLoading(true)
            apiClient.setAuthToken(apiToken)
            
            const response = await apiClient.getProjects()
            const allProjects = response.projects || []
            
            // Filter out projects the member already has access to
            const memberProjectIds = memberProjects.map(p => p.id)
            const available = allProjects.filter(project => 
                !memberProjectIds.includes(project.id) && project.status === 'active'
            )
            
            setAvailableProjects(available)
        } catch (error) {
            console.error('Failed to load available projects:', error)
            setAvailableProjects([])
        } finally {
            setLoading(false)
        }
    }

    const handleProjectChange = (e) => {
        const projectId = e.target.value
        if (projectId === '') {
            setSelectedProject(null)
        } else {
            const project = availableProjects.find(p => p.id === projectId)
            setSelectedProject(project)
        }
    }

    const handleSave = async () => {
        if (!selectedProject || !member) return

        try {
            setSaving(true)
            apiClient.setAuthToken(apiToken)
            
            const response = await apiClient.addUserToProject(selectedProject.id, member.id)
            
            onSave(selectedProject, response)
            onClose()
        } catch (error) {
            console.error('Failed to add member to project:', error)
            // Error handling is delegated to parent component
        } finally {
            setSaving(false)
        }
    }

    const handleCancel = () => {
        setSelectedProject(null)
        onClose()
    }

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Add to Project</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleCancel}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {member && (
                        <div className={styles.memberInfo}>
                            <span className={styles.memberLabel}>Adding:</span>
                            <span className={styles.memberName}>{member.full_name}</span>
                        </div>
                    )}

                    <div className={styles.formSection}>
                        <div className={styles.formGroup}>
                            <label htmlFor="projectSelect" className={styles.label}>
                                Project
                            </label>
                            {loading ? (
                                <div className={styles.loadingMessage}>
                                    Loading available projects...
                                </div>
                            ) : availableProjects.length === 0 ? (
                                <div className={styles.noProjectsMessage}>
                                    No available projects to add this member to.
                                </div>
                            ) : (
                                <select
                                    id="projectSelect"
                                    className={styles.select}
                                    value={selectedProject?.id || ''}
                                    onChange={handleProjectChange}
                                    disabled={loading || saving}
                                >
                                    <option value="">Select a project</option>
                                    {availableProjects.map(project => (
                                        <option key={project.id} value={project.id}>
                                            {project.name}
                                            {project.client_name && ` (${project.client_name})`}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {selectedProject && (
                            <div className={styles.selectionPreview}>
                                <span className={styles.previewLabel}>Selected Project:</span>
                                <span className={styles.previewText}>
                                    {selectedProject.name}
                                    {selectedProject.client_name && ` (${selectedProject.client_name})`}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <div className={styles.rightButtons}>
                        <button
                            type="button"
                            className={styles.cancelButton}
                            onClick={handleCancel}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={styles.confirmButton}
                            onClick={handleSave}
                            disabled={!selectedProject || loading || saving}
                        >
                            {saving ? 'Adding...' : 'Add to Project'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}